import {PaymentAuthorisationType, WalletPaymentErrorType, WalletPaymentSubmissionFailure, WalletPaymentSubmissionResult, WalletProvider} from '../payment';
import {PaymentModel} from "../payment.model";
import {ConfigurationModel, ParamConfig} from "../../configuration/configuration.model";
import {WalletPaymentProcessor} from "./wallet-payment-processor";

export class StripeWalletPaymentProcessor extends WalletPaymentProcessor {
    private stripe: any;
    private stripePaymentRequest: any;

    constructor(paymentModel: PaymentModel, configurationModel: ConfigurationModel) {
        super(paymentModel, configurationModel);
    }

    public async init(): Promise<WalletPaymentProcessor> {
        this.quickPayAvailable = false;
        if (!this.paymentModel.paymentPlatformPublicKey()) {
            return undefined;
        }

        // noinspection JSUnresolvedReference
        try {
            this.stripe = new (<any>window).Stripe(this.paymentModel.paymentPlatformPublicKey());
            this.stripePaymentRequest = this.stripe.paymentRequest({
                country: "AU",
                currency: "aud",
                total: {
                    label: "Order total",
                    // payment value will be updated on submission
                    amount: 100
                },
                requestPayerName: this.paymentModel.requestPayerName() || this.paymentModel.requestPayerPostcode(), // Either way, it just requires billing details
                requestPayerEmail: this.paymentModel.requestPayerEmail(),
                requestPayerPhone: this.paymentModel.requestPayerPhone()
            });
        } catch (error) {
            console.error("Stripe wallet payment initialisation failed", error);
            return undefined;
        }

        const paramConfig: ParamConfig = await this.paymentModel.configurationModel.getParamConfig();
        this.walletProvider = paramConfig.forcePay === "apple" ? WalletProvider.APPLE_PAY
            : paramConfig.forcePay === "google" ? WalletProvider.GOOGLE_PAY
                : undefined;
        if (this.walletProvider == undefined) {
            // noinspection JSUnresolvedReference
            const availability: any = await this.stripePaymentRequest.canMakePayment();

            // noinspection JSUnresolvedReference
            try {
                this.walletProvider = !availability ? undefined
                    : availability.applePay ? WalletProvider.APPLE_PAY
                        : availability.googlePay ? WalletProvider.GOOGLE_PAY
                            : undefined;
            } catch (error) {
                console.error("Failed to check wallet availability using Stripe", error);
            }
        }
        console.log("Wallet payment provider =", this.walletProvider);
        if (this.walletProvider == undefined) {
            return undefined;
        }
        this.quickPayAvailable = true;

        this.stripePaymentRequest.on("paymentmethod", (paymentResponse: any) => {
            try {
                paymentResponse.complete("success");
                this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider);
                this.resolveSubmission(new WalletPaymentSubmissionResult(PaymentAuthorisationType.STRIPE_PAYMENT_METHOD, paymentResponse.paymentMethod.id,
                    paymentResponse.payerName, paymentResponse.payerEmail, paymentResponse.payerPhone, paymentResponse.paymentMethod.billing_details.address.postal_code));
            } catch (error) {
                this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.name, error.message, error.code));
            }
        });

        // noinspection JSUnusedLocalSymbols
        this.stripePaymentRequest.on("cancel", () => {
            this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider);
            this.rejectSubmission(new WalletPaymentSubmissionFailure(this.walletProvider === WalletProvider.APPLE_PAY ? WalletPaymentErrorType.APPLE_PAY_CANCELLED : WalletPaymentErrorType.GOOGLE_PAY_CANCELLED));
        });
        return this;
    }

    // noinspection JSUnusedLocalSymbols
    public async showPaymentSheet(amount: number): Promise<void> {
        try {
            this.stripePaymentRequest.update({
                total: {
                    label: "Order total" + (this.paymentModel.paymentPlatformPublicKey().startsWith("pk_test") ? "(Test)" : ""),
                    amount: Math.round(amount * 100),
                }
            });
        } catch (error) {
            await this.init();
            this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.name, error.message, error.code));
            return;
        }

        try {
            await this.stripePaymentRequest.show();
        } catch (error) {

            // We're currently only expecting a Google Pay error here. Apple Pay reports all errors via the stripe callback.
            this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.name, error.message, error.code));
        }
    }
}
